import { createPortal } from 'react-dom';
import React, {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
  ReactNode,
  FunctionComponent,
  MouseEventHandler,
} from 'react';
import styles from './Modal.module.css';

const isBrowser = typeof window !== 'undefined';

function useRootBehavior(root: any) {
  useLayoutEffect(() => {
    if (!root) {
      return undefined;
    }
    const scrollY = window.scrollY;
    const previousPosition = document.body.style.position;
    const previousTop = document.body.style.top;
    const previousWidth = document.body.style.width;

    root.classList.add(styles.root);

    root.dataset.modalState = 'active';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.position = previousPosition;
      document.body.style.top = previousTop;
      document.body.style.width = previousWidth;
      delete root.dataset.modalState;
      window.scrollTo(0, scrollY);
    };
  }, [root]);
}

type ModalProps = {
  children: ReactNode;
  onClose?: (val: any) => void;
  noDismiss?: boolean;
  onDismiss?: (val: any) => void;
};

/**
 * Inspiration: https://codepen.io/gaearon/pen/yzMaBd
 */
const Modal: FunctionComponent<ModalProps> = ({
  children,
  root = (isBrowser && document.querySelector('body')) || undefined,
  onClose,
  noDismiss = false,
}: any) => {
  const [big, setBig] = useState(false);

  useLayoutEffect(() => {
    setBig(true);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        onClose && onClose(e);
      }
    };
    window?.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const contentRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
  const outsideClickHandler = useCallback<MouseEventHandler>(
    e => {
      if (noDismiss) {
        return;
      }
      if (!contentRef.current) {
        return;
      }
      if (e.target === contentRef?.current?.parentElement) {
        delete root.dataset.modalState;
        setBig(false);
        e.stopPropagation();
        onClose && onClose(e);
      }
    },
    [onClose]
  );

  useRootBehavior(root);
  if (!(isBrowser && root)) {
    return null;
  }

  const modal = (
    <div
      className={styles.container}
      style={{
        opacity: big ? 1 : 0,
        transition: 'all 100ms',
      }}
      onClick={outsideClickHandler}
    >
      <div
        className={styles.content}
        style={{
          transition: 'all 200ms ',
          transform: big ? 'scale(1,1)' : 'scale(0,0)',
          transitionTimingFunction: 'cubic-bezier(0,.62,.25,1)',
        }}
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog_label"
        aria-describedby="dialog_description"
      >
        {children}
      </div>
    </div>
  );

  return createPortal(modal, root);
};
export default Modal;
